import { useState } from "react";
import { Sparkles, Library, Trash2, Pencil, Check, X, MapPin } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

// Vue "Variantes perso" : gestion du catalogue de variantes créées par
// l'utilisateur (nom, abréviation, couleur) + visualisation/retrait des
// applications (sur une carte précise ou sur un set). L'application elle-même
// se fait depuis le set (panneau dédié) ou la fiche carte ; ici on gère le
// catalogue et on peut retirer une application existante.
export const CustomVariantsView = ({
  variants, applications, onCreate, onEdit, onRemove, onRemoveApplication, loadError, onReload,
}) => {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editAbbr, setEditAbbr] = useState("");
  const [editColor, setEditColor] = useState("#f59e0b");

  const appsByVariant = applications.reduce((acc, a) => {
    (acc[a.variant_id] = acc[a.variant_id] || []).push(a);
    return acc;
  }, {});

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    await onCreate({ name });
    setNewName("");
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setEditName(v.name);
    setEditAbbr(v.abbr);
    setEditColor(v.color || "#f59e0b");
  };

  const confirmEdit = async (id) => {
    const name = editName.trim();
    if (!name) return;
    await onEdit(id, { name, abbr: editAbbr.trim(), color: editColor });
    setEditingId(null);
  };

  const describeApp = (a) => {
    if (a.scope === "card") return `Carte ${a.card_id}`;
    if (a.rarity) return `Set ${a.set_id} — ${a.rarity}`;
    return `Set ${a.set_id} (toutes les cartes)`;
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
        <Sparkles className="w-6 h-6 text-amber-400" />Variantes perso
      </h2>
      <p className="text-sm text-zinc-500 mb-5 max-w-2xl">
        Crée tes propres variantes (ex. « Reverse Poké Ball », « Cosmos Holo », un tampon).
        Applique-les ensuite à une carte précise (depuis sa fiche) ou à tout un set
        (depuis la collection, bouton « Variantes perso »). Elles comptent dans le
        mode <span className="text-white font-medium">Master Set +</span>.
      </p>
      {loadError && (
        <p className="text-sm text-amber-400 mb-5">
          Tes variantes perso n'ont pas pu être chargées ({loadError}).{" "}
          <button className="underline hover:text-white" onClick={() => onReload?.()}>Réessayer</button>
        </p>
      )}

      <div className="flex gap-2 mb-6">
        <Input placeholder="Nom de la nouvelle variante..." value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
          className="max-w-sm" />
        <Button className="bg-red-600 hover:bg-red-500" disabled={!newName.trim()} onClick={handleCreate}>
          Créer
        </Button>
      </div>

      {variants.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Library className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Aucune variante perso pour l'instant. Crée-en une ci-dessus.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {variants.map(v => {
            const apps = appsByVariant[v.id] || [];
            const editing = editingId === v.id;
            return (
              <div key={v.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                {editing ? (
                  <div className="space-y-3">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                      placeholder="Nom" autoFocus />
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">Badge</span>
                        <Input value={editAbbr} maxLength={3}
                          onChange={(e) => setEditAbbr(e.target.value)}
                          className="w-16 text-center uppercase" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">Couleur</span>
                        <input type="color" value={editColor}
                          onChange={(e) => setEditColor(e.target.value)}
                          className="w-9 h-9 rounded cursor-pointer bg-transparent border border-zinc-700" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500"
                        onClick={() => confirmEdit(v.id)}>
                        <Check className="w-4 h-4 mr-1" />Enregistrer
                      </Button>
                      <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white"
                        onClick={() => setEditingId(null)}>
                        <X className="w-4 h-4 mr-1" />Annuler
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-8 h-8 rounded-md flex items-center justify-center text-xs font-extrabold text-white shrink-0"
                        style={{ background: v.color }} title={v.name}>
                        {v.abbr}
                      </span>
                      <h3 className="text-lg font-semibold text-white truncate flex-1">{v.name}</h3>
                      <Button size="sm" variant="outline" className="border-zinc-700 text-white"
                        onClick={() => startEdit(v)} title="Modifier">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="border-zinc-700 text-red-400 hover:text-red-300"
                        onClick={() => onRemove(v.id)} title="Supprimer (retire aussi cette variante de la collection)">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {apps.length === 0 ? (
                      <p className="text-xs text-zinc-600">
                        Pas encore appliquée. Va sur une carte ou un set pour l'ajouter.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        <div className="text-xs text-zinc-500 uppercase tracking-wide">Appliquée à</div>
                        {apps.map(a => (
                          <div key={a.id} className="flex items-center gap-2 text-sm text-zinc-300 bg-zinc-800/50 rounded px-2 py-1">
                            <MapPin className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                            <span className="flex-1 truncate" title={describeApp(a)}>{describeApp(a)}</span>
                            <button onClick={() => onRemoveApplication(a.id)}
                              className="text-zinc-500 hover:text-red-400" title="Retirer cette application">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
